import CheckCircle from "@mui/icons-material/CheckCircle";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import { alpha, Box, Stack, Typography, useTheme } from "@mui/material";
import { ReactNode } from "react";

/**
 * Radio-style option card for one side of a CUPA conflict (per the UX mock):
 * one click persists that side. Selected = success green check + tint (the
 * product's "chosen/linked" idiom, cf. the matching grid's LinkedIndicator).
 *
 * `disabled` blocks the SELECT action only. Fixed-height per UX — the reason
 * UI lives OUTSIDE the card (row detail panel), so cards stay uniform.
 */
export function CupaOptionCard({
  label,
  selected,
  disabled = false,
  onSelect,
  children,
}: {
  label: string;
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
  children?: ReactNode;
}) {
  const theme = useTheme();
  const accent = theme.palette.success.main;
  const handleSelect = () => {
    if (!disabled) onSelect();
  };
  return (
    <Box
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={handleSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleSelect();
        }
      }}
      sx={{
        width: "100%",
        // Fixed height so the two sides of a row (and rows among themselves)
        // always match, regardless of label length (per UX).
        height: 88,
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        cursor: disabled ? "default" : "pointer",
        px: 1.5,
        py: 1,
        my: 0.75,
        borderRadius: 1.5,
        border: `1px solid ${selected ? accent : theme.palette.divider}`,
        bgcolor: selected ? alpha(accent, 0.08) : theme.palette.background.paper,
        transition: "all 0.15s ease-in-out",
        ...(disabled
          ? {}
          : {
              "&:hover": { borderColor: accent, bgcolor: alpha(accent, selected ? 0.12 : 0.04) },
            }),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        {selected ? (
          <CheckCircle fontSize="small" color="success" sx={{ mt: 0.25 }} />
        ) : (
          <RadioButtonUnchecked
            fontSize="small"
            sx={{ mt: 0.25, color: disabled ? "action.disabled" : "text.disabled" }}
          />
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" color={disabled && !selected ? "text.disabled" : undefined}>
            {label}
          </Typography>
          {children}
        </Box>
      </Stack>
    </Box>
  );
}
